import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { useFlow } from "@/contexts/FlowContext";
import { useCurrentMedia } from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { Flows, DeploySteps, BuildSteps } from "@/types/flows";
import { useFlowState } from "@/hooks/useFlowState";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { ActionCard } from "./ActionCard";
import { ErrorInfo } from "@/components/ErrorInfo";
import Button from "@scripts/react/components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useModel } from "@/contexts/ModelContext";

function usePublishWorkflow(disabled: boolean) {
    const { task } = useFlow();
    const { setStep } = useFlowState();
    const { createRapidWorkflowDeploy, creatingRapidWorkflow } = useDeployContext();
    const { currentInferenceSampledData, hasEnoughDataToDeploy } = useComputedValues();
    const { user } = useAuth();
    const { currentWorkspace } = useCurrentWorkspace();

    const handleCreateRapidWorkflowDeploy = useCallback(async () => {
        if (disabled) return;

        if (!user) {
            setStep({ flow: Flows.BUILD, step: BuildSteps.PREVIEW });
            return;
        }

        try {
            const workflow = await createRapidWorkflowDeploy({
                task,
                workspaceId: currentWorkspace?.id,
                groundTruthData: currentInferenceSampledData
            });
            setStep({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY, workflowUrl: workflow.url });
            return workflow;
        } catch (err) {
            console.error("usePublishWorkflow createRapidWorkflow error", err);
            throw new Error(`There was an error creating your workflow. Please try again. ${err}`);
        }
    }, [
        createRapidWorkflowDeploy,
        currentInferenceSampledData,
        currentWorkspace?.id,
        disabled,
        setStep,
        task,
        user
    ]);

    const {
        isLoading: isLoadingCreateRapidWorkflow,
        error: errorCreateRapidWorkflow,
        invoke: invokeCreateRapidWorkflow
    } = useAsyncBoundOperation(handleCreateRapidWorkflowDeploy);

    const isLoading = isLoadingCreateRapidWorkflow || creatingRapidWorkflow;
    const isDisabled = disabled || isLoading || !hasEnoughDataToDeploy;

    const handlePublishClick = useCallback(() => {
        if (isDisabled) return;
        invokeCreateRapidWorkflow();
    }, [invokeCreateRapidWorkflow, isDisabled]);

    const handleRetry = useCallback(() => {
        handlePublishClick();
    }, [handlePublishClick]);

    return {
        handlePublishClick,
        handleRetry,
        isLoading,
        isDisabled,
        error: errorCreateRapidWorkflow,
        isLoadingCreateRapidWorkflow,
        hasEnoughDataToDeploy
    };
}

interface PublishErrorDisplayProps {
    error: Error | null;
    retrying: boolean;
    onRetryClick: () => void;
}

function PublishErrorDisplay({ error, retrying, onRetryClick }: PublishErrorDisplayProps) {
    if (!error) return null;

    return <ErrorInfo error={error} retrying={retrying} onRetryClick={onRetryClick} />;
}

interface PublishActionCardProps {
    disabled?: boolean;
}

export const PublishActionCard = ({ disabled = false }: PublishActionCardProps) => {
    const {
        handlePublishClick,
        handleRetry,
        isDisabled,
        isLoading,
        error: errorCreateRapidWorkflow,
        isLoadingCreateRapidWorkflow
    } = usePublishWorkflow(disabled);

    useKeyboardShortcut({
        key: "Enter",
        handler: handlePublishClick,
        disabled: isDisabled,
        requireMeta: true
    });

    return (
        <>
            <PublishErrorDisplay
                error={errorCreateRapidWorkflow}
                retrying={isLoadingCreateRapidWorkflow}
                onRetryClick={handleRetry}
            />
            <ActionCard
                title="Use Model"
                description={"Start using your model now."}
                icon={isLoading ? "far fa-spinner fa-spin" : "far fa-rocket"}
                type="primary"
                onClick={handlePublishClick}
                disabled={isDisabled}
                hotkeys={["⌘", "⏎"]}
            />
        </>
    );
};

interface PublishActionButtonProps {
    disabled?: boolean;
}

export const PublishActionButton = ({ disabled = false }: PublishActionButtonProps) => {
    const currentMedia = useCurrentMedia();
    const { isDownloading } = useModel();
    const { imagesNeededToDeploy } = useComputedValues();
    const {
        handlePublishClick,
        handleRetry,
        isLoading,
        isDisabled: isButtonDisabled,
        error: errorCreateRapidWorkflow,
        isLoadingCreateRapidWorkflow,
        hasEnoughDataToDeploy
    } = usePublishWorkflow(disabled);

    const tooltipTitle = !hasEnoughDataToDeploy
        ? `Upload ${imagesNeededToDeploy} more image${imagesNeededToDeploy !== 1 ? "s" : ""} to publish your model`
        : undefined;

    useKeyboardShortcut({
        key: "Enter",
        handler: handlePublishClick,
        disabled: isButtonDisabled,
        requireMeta: true
    });

    const buttonContent = (
        <Button
            type={!isDownloading ? "primary" : "secondary"}
            disabled={isButtonDisabled}
            loading={isLoading}
            onClick={handlePublishClick}
            className="w-full"
        >
            <i className="far fa-rocket"></i>
            <span>Publish and Use Model</span>
            <div
                className={twClassNames(
                    "hotkeysContainer",
                    (isButtonDisabled || isLoading) && "opacity-30"
                )}
            >
                <span className="hotkey dark-brand">⌘</span>
                <span className="hotkey dark-brand">⏎</span>
            </div>
        </Button>
    );

    return (
        <>
            <PublishErrorDisplay
                error={errorCreateRapidWorkflow}
                retrying={isLoadingCreateRapidWorkflow}
                onRetryClick={handleRetry}
            />
            {isButtonDisabled && !isLoading && !currentMedia?.file.isVideo ? (
                <TooltipContainer
                    type="titleOnly"
                    title={tooltipTitle}
                    customButton={<div className="w-full">{buttonContent}</div>}
                />
            ) : (
                buttonContent
            )}
        </>
    );
};
